import {
  QLD_RVM_CATEGORY_COLORS,
  QLD_RVM_CATEGORY_LABELS,
} from "../map/context-layer-meta";
import type { DeforestationLayerStates } from "../types/deforestation";

// Palette entries shown in the bottom-of-card legend. Colours mirror the
// pre-rendered raster tiles' baked palettes — keep these in lockstep with
// the python tilers (tile-forest-raster.py / tile-lulc-raster.py /
// tile-forest-change-raster.py / fetch-a14-nvis.py).
//
// Only raster layers that bake a categorical palette into their tiles
// belong here. Vector context layers (Cadastre, CAPAD, Ramsar, …) carry
// a single user-pickable colour shown next to the layer toggle and don't
// need a legend.

export interface LegendEntry {
  label: string;
  color: string;
}

export interface LegendDefinition {
  title: string;
  entries: LegendEntry[];
}

export const LAYER_LEGENDS: Partial<
  Record<keyof DeforestationLayerStates, LegendDefinition>
> = {
  deforestationRisk: {
    title: "Forest Loss 2025",
    entries: [{ label: "Forest loss", color: "#dc2626" }],
  },
  forest2020: {
    title: "Forest",
    entries: [{ label: "Forest", color: "#018933" }],
  },
  forest2025: {
    title: "Forest",
    entries: [{ label: "Forest", color: "#018933" }],
  },
  crownCover2020: {
    title: "Crown Cover",
    entries: [
      { label: "Closed Forest", color: "#006400" },
      { label: "Open Forest", color: "#32CD32" },
      { label: "Woodland", color: "#228B22" },
      { label: "Unknown Forest", color: "#7AAE7A" },
    ],
  },
  crownCover2025: {
    title: "Crown Cover",
    entries: [
      { label: "Closed Forest", color: "#006400" },
      { label: "Open Forest", color: "#32CD32" },
      { label: "Woodland", color: "#228B22" },
      { label: "Unknown Forest", color: "#7AAE7A" },
    ],
  },
  forestChange: {
    title: "Forest Change (2020–2025)",
    entries: [
      { label: "Tree Loss", color: "#8B4513" },
      { label: "Tree Gain", color: "#018933" },
    ],
  },
  lulc2025: {
    title: "Land Use 2025",
    entries: [
      { label: "Forest", color: "#018933" },
      { label: "Plantation/Trees", color: "#01896e" },
      { label: "Range Land", color: "#3dbf35" },
      { label: "Pasture/Fallow", color: "#6ee16e" },
      { label: "Crop Land", color: "#fdf06f" },
      { label: "Barren Land", color: "#fdae61" },
      { label: "Built Up", color: "#e75a5a" },
      { label: "Waterbody", color: "#003fbf" },
    ],
  },
  nvisVegetation: {
    title: "NVIS Vegetation",
    entries: [
      { label: "Closed forest", color: "#0F5232" },
      { label: "Open forest", color: "#248A3C" },
      { label: "Sparse forest", color: "#6FA85C" },
      { label: "Closed woodland", color: "#35963F" },
      { label: "Woodland (open)", color: "#7CBE60" },
      { label: "Sparse woodland", color: "#A8C86F" },
      { label: "Heath", color: "#A98E4F" },
      { label: "Closed shrubland", color: "#B6976A" },
      { label: "Shrubland", color: "#C2A46F" },
      { label: "Sparse shrubland", color: "#D4BF8C" },
      { label: "Hummock grass", color: "#D9CB65" },
      { label: "Tussock grass", color: "#C2C255" },
      { label: "Grass / herbland", color: "#BEC45C" },
      { label: "Sedge / rush / forb", color: "#6FB29F" },
      { label: "Aquatic / mangrove", color: "#356EA0" },
    ],
  },
  regulatedVeg: {
    title: "Regulated Vegetation (QLD VMA category)",
    entries: Object.entries(QLD_RVM_CATEGORY_COLORS).map(([cat, color]) => ({
      label: QLD_RVM_CATEGORY_LABELS[cat] ?? cat,
      color,
    })),
  },
};
